/**
 * A ARITMÉTICA DA PESAGEM — separada da tela para poder ser testada.
 *
 * Mora aqui, e não dentro do componente, pelo mesmo motivo que todo cálculo
 * deste projeto mora em `dominio/`: é a classe de erro mais cara do sistema e
 * a mais silenciosa. Uma média errada não quebra nada — ela sai do app como
 * número medido, ancora a curva do ciclo e fica meses sem ninguém desconfiar.
 *
 * Cinco telas de manejo dividem estas contas: biometria avulsa, transferência,
 * despesca, recepção e repicagem. Fórmula copiada em cinco arquivos diverge um
 * dia.
 *
 * As duas regras que os testes fixam (`testes/pesagem.mjs`):
 *
 *   1. A MÉDIA É PONDERADA. Soma tudo e divide UMA vez, nunca tira a média das
 *      médias — um bloco de 30 peixes e outro de 5 não valem o mesmo.
 *   2. VALOR IMPOSSÍVEL NUNCA VIRA MÉDIA. Bloco incompleto, tara maior que o
 *      bruto, quantidade fora da faixa: devolve nulo e some da soma. A mesma
 *      regra do campo de oxigênio, em `testes/campos.mjs`.
 */

import { ilegivel, inteiroNaFaixa, numero, numeroNaFaixa, soNumero } from './unidades'

export interface Bloco {
  /** Só para o React distinguir as linhas; não vai para o banco. */
  chave: string
  quantidade: string
  bruto: string
  tara: string
  /**
   * Só na despesca: o cestão sai misturado e cada espécie tem preço próprio.
   * Ausente nas outras telas, onde o peixe é o do ciclo e dizer a espécie a
   * cada cestão seria digitar a mesma palavra quarenta vezes.
   */
  especie?: string
}

export interface Apuracao {
  peixes: number
  liquidoKg: number
  mediaG: number
}

export function blocoVazio(taraAnterior = '', especieAnterior?: string): Bloco {
  return {
    chave: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    quantidade: '',
    bruto: '',
    tara: taraAnterior,
    // A espécie repete a do cestão anterior: 99% é tambaqui, e trocar é a
    // exceção. O mesmo argumento da tara pré-preenchida.
    ...(especieAnterior === undefined ? {} : { especie: especieAnterior }),
  }
}

/**
 * Os limites do bom senso da fazenda, e o do servidor.
 *
 * MAX_MEDIA_G é o único que não é bom senso: é o teto que `POST /api/biometrias`
 * aplica, e está repetido aqui de propósito. Sem ele a tela aceitava 20 peixes
 * com 500 kg — 25.000 g por peixe —, punha na fila, e a recusa só aparecia
 * quando alguém apertasse Sincronizar, dias depois, com o peixe de volta na
 * água e a medição impossível de refazer.
 */
export const MAX_PEIXES = 2000
export const MAX_KG = 2000
export const MAX_MEDIA_G = 20_000

/** O que um bloco vale sozinho, ou nulo se ainda está incompleto. */
export function apurarBloco(b: Bloco, comTara: boolean): Apuracao | null {
  const peixes = inteiroNaFaixa(b.quantidade, 1, MAX_PEIXES)
  const bruto = numeroNaFaixa(b.bruto, 0.001, MAX_KG)
  if (peixes === null || bruto === null) return null

  const tara = taraDoBloco(b, comTara)
  if (tara === null) return null

  const liquidoKg = bruto - tara
  if (liquidoKg <= 0) return null

  const mediaG = (liquidoKg * 1000) / peixes
  if (mediaG > MAX_MEDIA_G) return null

  return { peixes, liquidoKg, mediaG }
}

/**
 * A tara do bloco: zero quando EM BRANCO, nulo quando ilegível.
 *
 * A distinção é a correção de 20/09/2026, e ela vale o comentário. Antes, uma
 * tara que não virava número caía num `?? 0` e o peso do cesto entrava somado
 * ao do peixe — o acidente exato que o campo existe para impedir, produzido
 * pela defesa que deveria impedi-lo. E o estrago passava despercebido: o campo
 * ficava preenchido (parece certo), o bloco seguinte herdava a mesma string
 * quebrada, e a média errava só uns 12% — pouco para saltar aos olhos, muito
 * para ancorar a curva do ciclo.
 *
 * Branco valendo zero continua certo: cesto que não se pesou não existe.
 * Digitado e ilegível não pode valer zero.
 */
function taraDoBloco(b: Bloco, comTara: boolean): number | null {
  // Onde a tara está escondida, a balança já entregou o líquido.
  if (!comTara) return 0
  if (b.tara.trim() === '') return 0
  return numeroNaFaixa(b.tara, 0, MAX_KG)
}

/**
 * Por que este bloco não fecha — em uma frase, para a tela dizer.
 *
 * Campo que fica em branco sem explicação faz a pessoa digitar de novo igual.
 * Com o padrão do sistema recém-fixado (vírgula decimal, ponto de milhar),
 * dizer «use vírgula» é o que transforma a recusa em aprendizado.
 */
export function porQueNaoApura(b: Bloco, comTara: boolean): string | null {
  if (apurarBloco(b, comTara)) return null

  for (const campo of [b.quantidade, b.bruto, comTara ? b.tara : '']) {
    if (ilegivel(campo)) return 'use vírgula para o decimal: 19,2'
  }

  const peixes = numeroNaFaixa(b.quantidade, 1, MAX_PEIXES)
  if (peixes !== null && !Number.isInteger(peixes)) return 'peixe não tem metade — conte inteiro'

  if (b.quantidade.trim() === '' || b.bruto.trim() === '') return null

  const bruto = numeroNaFaixa(b.bruto, 0.001, MAX_KG)
  const tara = taraDoBloco(b, comTara)
  if (bruto !== null && tara !== null && bruto - tara <= 0) {
    return 'a tara está maior que o peso — confira qual é o cesto'
  }

  const a = peixes !== null && bruto !== null && tara !== null
    ? ((bruto - tara) * 1000) / peixes
    : null
  if (a !== null && a > MAX_MEDIA_G) return 'essa conta dá mais de 20 kg por peixe — confira'

  return 'confira os números'
}

/** O que os blocos valem juntos. Soma tudo, divide uma vez. */
export function apurar(blocos: Bloco[], comTara: boolean): Apuracao | null {
  let peixes = 0
  let liquidoKg = 0
  for (const b of blocos) {
    const a = apurarBloco(b, comTara)
    if (a) {
      peixes += a.peixes
      liquidoKg += a.liquidoKg
    }
  }
  if (peixes === 0 || liquidoKg <= 0) return null
  const mediaG = (liquidoKg * 1000) / peixes
  if (mediaG > MAX_MEDIA_G) return null
  return { peixes, liquidoKg, mediaG }
}

/**
 * «2,67 kg» para peixe graúdo, «573 g» para o resto.
 *
 * Duas casas, e não três, por causa de um número: com três, um peixe de um
 * quilo aparecia como «1,000 kg» — que em português se lê como mil. Num app
 * onde 1.000 g e 1,000 kg convivem na mesma tela, essa vírgula é uma armadilha.
 * Duas casas dão resolução de 10 g, que num peixe de 2 kg é 0,5% — e o erro
 * que este número existe para denunciar (o cesto não tarado) vale dezenas de
 * gramas por peixe, nunca dez.
 */
export function pesoPorExtenso(mediaG: number): string {
  return mediaG >= 1000 ? `${numero(mediaG / 1000, 2)} kg` : `${numero(mediaG, 0)} g`
}

/** O que o campo aceita digitar. Re-exportado para a tela não importar dois lugares. */
export { soNumero }
